/**
 * Auth module - JWT auth with automatic token refresh.
 * This replaced the old Keycloak integration; the filename and API
 * now match the current auth model.
 */

let refreshTimer = null;
let isRefreshing = false;
let refreshPromise = null;

const clearStoredAuth = () => {
  localStorage.removeItem('nf_token');
  localStorage.removeItem('nf_refresh_token');
};

const authClient = {
  token: null,
  authenticated: false,
  tokenParsed: null,
  login: () => { window.location.href = '/login'; },
  logout: () => { window.location.href = '/'; },
  updateToken: () => refreshToken(),
};

export const getAuthHeader = () => {
  const token = localStorage.getItem('nf_token');
  return token ? `Bearer ${token}` : null;
};

/**
 * Parse JWT payload without verification
 */
const parseToken = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

/**
 * Get seconds until token expires (negative if already expired)
 */
const getTokenTTL = () => {
  const token = localStorage.getItem('nf_token');
  if (!token) return -1;
  const payload = parseToken(token);
  if (!payload || !payload.exp) return -1;
  return payload.exp - Math.floor(Date.now() / 1000);
};

export const getUser = () => {
  const token = localStorage.getItem('nf_token');
  if (!token) return null;
  const payload = parseToken(token);
  if (!payload) return null;
  return {
    id: payload.id,
    email: payload.username,
    firstName: payload.username,
    name: payload.username,
    roles: payload.scope || [],
  };
};

export const hasRole = (role) => {
  const user = getUser();
  return user && user.roles.includes(role);
};

export const isAuthenticated = () => {
  const token = localStorage.getItem('nf_token');
  if (!token) return false;
  // Check if token is expired
  const ttl = getTokenTTL();
  if (ttl < 0) {
    // Token expired — try to use refresh token if available
    const rt = localStorage.getItem('nf_refresh_token');
    if (rt) {
      // Still "authenticated" — refresh will happen on next API call
      return true;
    }
    // No refresh token — clean up
    clearStoredAuth();
    return false;
  }
  return true;
};

export const login = () => {
  window.location.href = '/login';
};

export const logout = () => {
  clearRefreshTimer();
  localStorage.removeItem('nf_token');
  localStorage.removeItem('nf_refresh_token');
  window.location.href = '/';
};

/**
 * Refresh the access token using the stored refresh token.
 * Returns a promise that resolves with the new token or rejects.
 * Deduplicates concurrent refresh calls.
 */
export const refreshToken = () => {
  // If already refreshing, return the existing promise
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  const rt = localStorage.getItem('nf_refresh_token');
  if (!rt) {
    return Promise.reject(new Error('No refresh token'));
  }

  isRefreshing = true;
  refreshPromise = fetch('/auth0/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: rt }),
  })
    .then((response) => {
      if (!response.ok) {
        const error = new Error('Refresh failed');
        error.status = response.status;
        throw error;
      }
      return response.json();
    })
    .then((data) => {
      localStorage.setItem('nf_token', data.token);
      if (data.refresh_token) {
        localStorage.setItem('nf_refresh_token', data.refresh_token);
      }
      scheduleRefresh();
      return data.token;
    })
    .catch((err) => {
      // Only clear auth when the refresh token is actually invalid.
      if (err.status === 400 || err.status === 401 || err.status === 403 || err.message === 'No refresh token') {
        clearStoredAuth();
        window.location.replace('/login');
      }
      throw err;
    })
    .finally(() => {
      isRefreshing = false;
      refreshPromise = null;
    });

  return refreshPromise;
};

/**
 * Schedule a proactive token refresh 2 minutes before expiry
 */
export const scheduleRefresh = () => {
  clearRefreshTimer();
  const ttl = getTokenTTL();
  if (ttl <= 0) return;

  // Refresh 2 minutes before expiry, minimum 10 seconds from now
  const refreshIn = Math.max((ttl - 120) * 1000, 10000);
  refreshTimer = setTimeout(() => {
    refreshToken().catch(() => {});
  }, refreshIn);
};

const clearRefreshTimer = () => {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
};

/**
 * Initialize — schedule refresh if we have a valid token
 */
export const initAuth = () => {
  if (localStorage.getItem('nf_token')) {
    scheduleRefresh();
  }
};

/**
 * Fetch wrapper that auto-refreshes token on 401 responses.
 * Drop-in replacement for fetch() with Authorization header.
 */
export const authFetch = async (url, options = {}) => {
  const token = localStorage.getItem('nf_token');
  var headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  let response = await fetch(url, { ...options, headers });

  if (response.status === 401 && token) {
    try {
      const newToken = await refreshToken();
      headers['Authorization'] = `Bearer ${newToken}`;
      response = await fetch(url, { ...options, headers });
    } catch (e) {
      clearStoredAuth();
      throw e;
    }
  }
  return response;
};

export const createApiClient = () => {
  const axios = require('axios');
  const apiClient = axios.create({
    baseURL: '/api',
    timeout: 10000,
  });

  apiClient.interceptors.request.use((config) => {
    const authHeader = getAuthHeader();
    if (authHeader) {
      config.headers.Authorization = authHeader;
    }
    return config;
  });

  apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const newToken = await refreshToken();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } catch (refreshErr) {
          // refreshToken already handles redirect to /login
          return Promise.reject(refreshErr);
        }
      }
      return Promise.reject(error);
    }
  );

  return apiClient;
};

export default authClient;
